import { NavLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';
import { LeadStatusSelector } from '../../../scenes/leads/status/LeadStatusSelector';
import { Status } from '../../../components/special/Status';
import { useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../../../components/exo/ExoDialog';
import { LeadMeetingHistory } from '../../../scenes/leads/lead-modules/LeadMeetingHistory';
import Feedback from '../../../components/special/Feedback';
import { NewInteraction } from '../../../components/new/NewInteraction';
import { NewInteractionNote } from '../../../components/new/NewInteractionNote';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import moment from 'moment';
import { ExoTasks } from '../../../components/exo/ExoTasks';
import { NewTask } from '../../../components/new/NewTask';
import { ProjectInteractionHistory } from '../../../scenes/projects/ProjectInteractionHistory';
import { ProjectQuickActions } from '../../../scenes/projects/ProjectQuickActions';
import { ProjectMeetingHistory } from '../../../scenes/projects/ProjectMeetingHistory';
import ProjectParticipantsList from './ProjectParticipantsList';

export const ShowProjectsInteractions = ({
  project,
  editmode,
  events,
  onEventsChange,
  onProjectChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [interactions, setInteractions] = useState(null);

  const { name, image, participants, status, id } = project;

  useEffect(() => {
    // interactions
    DBRequest({
      config,
      path: `interactions?projectId[eq]=${id}`,
      onResponse: setInteractions,
    });
  }, [config, id]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    DBRequest({
      config,
      path: 'projects/' + project.id,
      method: API_PATCH({ status: status.status }),
      onResponse: onProjectChange,
    });
  }

  // edit
  const [editInteraction, setEditInteraction] = useState(null);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  // interactions change
  function updateInteractions(res) {
    const newInteractions = updateOrCreate(interactions, res.data);
    const newSortedInteractions = sortInteractions(newInteractions);

    setInteractions(newSortedInteractions);
  }

  function sortInteractions(inter) {
    const sortedInteractions = inter.data.sort((a, b) =>
      moment(b.dateTime).diff(moment(a.dateTime))
    );
    return { ...inter, data: sortedInteractions };
  }

  function handleLoadInteractions(type) {
    setInteractions(null);

    const path = !type
      ? `interactions?projectId[eq]=${project.id}`
      : `interactions?projectId[eq]=${project.id}&type[eq]=${type}`;

    DBRequest({
      config,
      path,
      onResponse: setInteractions,
    });
  }

  // events
  function updateEvents(res) {
    onEventsChange(updateOrCreate(events, res));
  }

  return (
    <>
      <Box className="pt-0 md:pt-4 flex flex-col xl:flex-row gap-2 xl:h-full md:overflow-y-auto xl:overflow-hidden">
        <Box className="grid grid-rows-[auto_1fr] gap-2 h-full">
          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md p-4 relative xl:min-w-[450px] xl:max-w-[500px] overflow-y-auto md:h-full lg:h-auto md:min-h-full lg:min-h-auto"
          >
            <Box className="flex gap-4 w-full">
              <ExoAvatar
                size="80px"
                picture={image?.temporaryUrl}
                name={name}
              />
              <Box className="w-full flex flex-col gap-2 items-start justify-start">
                <Box className="flex">
                  <Typography variant="h3" textAlign="left">
                    {name}
                  </Typography>
                </Box>
                <Status
                  text={
                    status === 0
                      ? t('Running')
                      : status === 1
                      ? t('Finished')
                      : status === 2
                      ? t('Cancelled')
                      : t('No Status')
                  }
                  variant={
                    status === 0
                      ? 'running'
                      : status === 1
                      ? 'finished'
                      : status === 2
                      ? 'rejected'
                      : t('No Status')
                  }
                  onClick={(e) => handleClick(e, project)}
                  disableMobile
                  fullWidth
                />
              </Box>
            </Box>

            <Box className="pt-2 pb-3">
              <Divider />
            </Box>

            <ProjectQuickActions
              isLoading={!project}
              name={name}
              data={project}
              type="project"
              typePlural="projects"
              onFeedback={handleFeedback}
              onMeetingChange={updateEvents}
              onInteractionChange={updateInteractions}
              noTasks={true}
            />
          </Box>
          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative xl:min-w-[450px] xl:max-w-[500px] overflow-y-auto md:h-full lg:h-auto md:min-h-full lg:min-h-auto flex flex-col"
          >
            <Typography fontWeight={600} className="pb-2">
              {t('Meetings')}
            </Typography>
            <ProjectMeetingHistory
              events={events}
              onEventsChange={onEventsChange}
              onInteractionChange={updateInteractions}
              onFeedback={handleFeedback}
              project={project}
            />
          </Box>
        </Box>

        <Box
          sx={{ bgcolor: colors.glass }}
          className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative xl:h-full flex flex-col w-full"
        >
          <Typography fontWeight={600} className="pb-2">
            {t('History')}
          </Typography>
          <ProjectInteractionHistory
            interactions={interactions}
            onEdit={(e) => setEditInteraction(e)}
            setInteractions={setInteractions}
            onFeedback={handleFeedback}
            loadData={handleLoadInteractions}
            projectId={project.id}
          />
        </Box>

        <Box
          sx={{ bgcolor: colors.glass }}
          className="rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative xl:h-full flex flex-col w-full max-w-full xl:max-w-[350px]"
        >
          <Typography fontWeight={600} className="pb-2">
            {t('Project Participants')}
          </Typography>
          <ProjectParticipantsList
            participants={participants}
            onProjectChange={onProjectChange}
            editmode={editmode}
            project={project}
          />
        </Box>
      </Box>

      <ExoDialog
        open={editInteraction && editInteraction.type !== 'note'}
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteraction
          startData={editInteraction}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
        />
      </ExoDialog>

      <ExoDialog
        open={
          editInteraction &&
          (editInteraction.type === 'note' || editInteraction.type === 'email')
        }
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteractionNote
          startData={editInteraction !== 'note' ? editInteraction : {}}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
          sourceType={editInteraction?.type}
        />
      </ExoDialog>
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Running'),
            status: 0,
            variant: t('running'),
          },
          {
            label: t('Finished'),
            status: 1,
            variant: t('finished'),
          },
          {
            label: t('Canceled'),
            status: 2,
            variant: t('rejected'),
          },
        ]}
      />
      <Feedback setState={setAlertState} state={alertState} />
    </>
  );
};
